import React, {
  useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { primaryColor, primaryFont, wMedium } from 'Utils/utilsStyle';

// width: 0 + min-width: 100% keeps the nowrap line from widening the flex column,
// so the cell always takes exactly the column width
const Wrapper = styled.div`
  width: 0;
  min-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  .names-line {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .linked-name {
    color: ${primaryColor};
    cursor: pointer;
  }
  .more-export {
    color: ${primaryColor};
    font-family: ${primaryFont};
    font-weight: ${wMedium};
    font-size: 14px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    margin-top: 2px;
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;

const SEPARATOR = ', ';
let measureCanvas: HTMLCanvasElement | null = null;

const measureText = (text: string, font: string) => {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const context = measureCanvas.getContext('2d');
  if (!context) return text.length * 7;
  context.font = font;
  return context.measureText(text).width;
};

// How many whole names fit on one line of the given width (at least one)
const getFittingCount = (names: string[], width: number, font: string) => {
  let used = 0;
  for (let i = 0; i < names.length; i += 1) {
    used += measureText(i === 0 ? names[i] : `${SEPARATOR}${names[i]}`, font);
    if (used > width) return Math.max(1, i);
  }
  return names.length;
};

export type NameItem = {
  name: string;
  // set only for mapped companies, which link to their company page
  id?: number;
};

type Props = {
  items: NameItem[];
  onNameClick?: (id: number) => void;
  hasMoreOnServer?: boolean;
  onMoreClick: () => void;
  disabled?: boolean;
  className?: string;
};

const FittedNamesCell = function FittedNamesCell(props: Props) {
  const {
    items, onNameClick, hasMoreOnServer, onMoreClick, disabled, className,
  } = props;
  const names = useMemo(() => items.map((item) => item.name), [items]);
  const lineRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(names.length);

  useLayoutEffect(() => {
    const element = lineRef.current;
    if (!element) return undefined;
    const update = () => {
      const { font } = window.getComputedStyle(element);
      setVisibleCount(getFittingCount(names, element.clientWidth, font));
    };
    update();
    // Column width shrinks as more companies are compared, so re-measure on resize
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [names]);

  if (!names.length) return <span className={className}>N/A</span>;

  const showMore = visibleCount < names.length || !!hasMoreOnServer;

  return (
    <Wrapper>
      <span ref={lineRef} className={`names-line ${className || ''}`} title={names.join(SEPARATOR)}>
        {items.slice(0, visibleCount).map((item, index) => (
          <React.Fragment key={item.id ? `id-${item.id}` : `name-${item.name}`}>
            {index ? SEPARATOR : null}
            {item.id && onNameClick ? (
              <span
                className="linked-name"
                role="button"
                tabIndex={0}
                onClick={() => onNameClick(item.id as number)}
                onKeyPress={() => onNameClick(item.id as number)}
              >
                {item.name}
              </span>
            ) : item.name}
          </React.Fragment>
        ))}
      </span>
      {showMore ? (
        <button
          type="button"
          className="more-export"
          onClick={onMoreClick}
          disabled={disabled}
        >
          More view in export
        </button>
      ) : null}
    </Wrapper>
  );
};

FittedNamesCell.defaultProps = {
  onNameClick: undefined,
  hasMoreOnServer: false,
  disabled: false,
  className: '',
};

export default React.memo(FittedNamesCell);
